import React, { useEffect, useState } from "react";
import logCompletedActivity from "./common/logCompletedActivity";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { selectNextTopic } from "../../store/selectors";
import Spinner from "../Spinner/Spinner";
import { setKeyTerm } from "../../store/actions";
import { useTranslation } from "react-i18next";
import { logEvent, logInteractionEvent } from "../../utils/appInsight";
import { trackAdobeAppClick, trackAdobeClick } from "../../utils/adobeLogging";
import ModulePageLayout from "../common/ModulePageLayout";
import { userFinishedTopicThunk } from "../../store/thunks";
import {
  isAllSubTopicsCompleted,
  isSectionFinished,
} from "../../utils/helpers";
import PopupNotification from "../popupnotification/PopupNotification";

const Activity = ({ activity, section, topic }) => {
  const [html, setHtml] = useState({ __html: "" });
  const [script, setScript] = useState("");
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const allTopics = useSelector((state) => Object.values(state.topics));
  const finishedTopics = useSelector((state) => state.progress.finishedTopics);
  const finishedSubTopics = useSelector(
    (state) => state.progress.finishedSubTopics
  );
  const parts = useSelector((state) => state.parts[topic.contentKey]) || [];
  const history = useHistory();
  const nextTopic = useSelector((state) => selectNextTopic(state, topic));
  const pmApproach = useSelector((state) => state.progress.section);
  const lang = useSelector((state) => state.ui.lang);

  const { t } = useTranslation();

  useEffect(() => {
    const doneHandler = () => {
      if (
        !finishedTopics.includes(topic.contentKey) &&
        isAllSubTopicsCompleted(finishedSubTopics, parts, topic)
      ) {
        setLoading(true);
        dispatch(
          userFinishedTopicThunk([...finishedTopics, topic.contentKey])
        ).then((finishedTopics) => {
          logEvent({
            name: "LessonCompleted",
            properties: {
              section: pmApproach,
              moduleKey: topic.parentContentId,
              topicKey: topic.contentKey,
            },
          });
          logInteractionEvent("LessonCompleted", {
            section: pmApproach,
            moduleKey: topic.parentContentId,
            topicKey: topic.contentKey,
          });
          logCompletedActivity(
            topic,
            finishedTopics,
            allTopics,
            pmApproach,
            dispatch
          );
          setLoading(false);
          trackAdobeClick(null, {
            linkTitle: t("nextLesson"),
            linkModule: `${pmApproach}-activity`,
            targetURL: `/app/${nextTopic.parentContentId}/${nextTopic.contentKey}`,
          });
          history.push(
            `/app/${nextTopic.parentContentId}/${nextTopic.contentKey}`
          );
        });
      } else {
        trackAdobeClick(null, {
          linkTitle: t("nextLesson"),
          linkModule: `${pmApproach}-activity`,
          targetURL: `/app/${nextTopic.parentContentId}/${nextTopic.contentKey}`,
        });
        history.push(
          `/app/${nextTopic.parentContentId}/${nextTopic.contentKey}`
        );
      }
    };

    const backHandler = () => {
      trackAdobeClick(null, {
        linkTitle: t("back"),
        linkModule: `${pmApproach}-activity`,
        targetURL: `/app/${section.contentKey}/${topic.contentKey}`,
      });
      history.push(`/app/${section.contentKey}/${topic.contentKey}/3`);
    };
    const keyTermHandler = (e) => {
      dispatch(setKeyTerm(e.detail.term));
    };
    const downloadHandler = () => {
      const topicData = topic.data;
      const downloadLabel = t("downloadTemplate");
      trackAdobeAppClick({ action: "download-template", language: lang });
      trackAdobeClick(
        { fileName: topicData.template.title, fileLanguage: lang },
        {
          linkTitle: downloadLabel,
          linkModule: `${pmApproach}-module-page`,
          targetURL: topicData.template.fileUrl,
        },
        false
      );
      logEvent({
        name: "TemplateDownloaded",
        properties: {
          pmApproach,
          template: topicData.template,
        },
      });
      logInteractionEvent("TemplateDownloaded", {
        pmApproach,
        template: topicData.template.fileUrl,
      });
    };
    const continueHandler = (event) => {
      trackAdobeClick(null, {
        linkTitle: `${topic.contentKey}:${t("continue")}-${
          event?.detail?.pageNumber || 0
        }`,
        linkModule: `${pmApproach}-activity`,
        targetURL: `/app/${section.contentKey}/${topic.contentKey}`,
      });
    };

    window.addEventListener("done", doneHandler);
    window.addEventListener("back", backHandler);
    window.addEventListener("keyTerm", keyTermHandler);
    window.addEventListener("download-template", downloadHandler);
    window.addEventListener("continue", continueHandler);

    return () => {
      window.removeEventListener("done", doneHandler);
      window.removeEventListener("back", backHandler);
      window.removeEventListener("keyTerm", keyTermHandler);
      window.removeEventListener("download-template", downloadHandler);
      window.removeEventListener("continue", continueHandler);
    };
  }, [
    allTopics,
    dispatch,
    topic,
    finishedTopics,
    pmApproach,
    history,
    nextTopic.parentContentId,
    nextTopic.contentKey,
    section.contentKey,
  ]);

  useEffect(() => {
    let fullContent = activity.fullContent;
    const scriptParts = /<script>(.+)<\/script>/gis.exec(fullContent);

    if (scriptParts && scriptParts[1]) {
      setScript(scriptParts[1]);
      fullContent = fullContent.replace(scriptParts[0], "");
    }

    setHtml({ __html: fullContent });
  }, [activity.fullContent]);

  useEffect(() => {
    if (html && html.__html !== "" && script !== "") {
      window.eval(script);
    }
    return () => {
      setScript("");
    };
  }, [html, script]);

  return (
    <>
      {!isSectionFinished(finishedTopics, allTopics, pmApproach) &&
        !isAllSubTopicsCompleted(finishedSubTopics, parts, topic) && (
          <PopupNotification />
        )}
      <div className="activity-page" adoberegion={`${pmApproach}-activity`}>
        <ModulePageLayout topic={topic} module={section} part={4}>
          {loading ? (
            <div className="flex justify-center mt-50">
              <div className="flex flex-col justify-center text-center">
                <div className="text-center flex justify-center">
                  <Spinner />
                </div>
                <p className="mt-10">{t("savingProgress")}</p>
              </div>
            </div>
          ) : (
            <div dangerouslySetInnerHTML={html} />
          )}
        </ModulePageLayout>
      </div>
    </>
  );
};

export default Activity;
